import React, { useEffect, useState } from 'react';
import Employee from '../types/api/Employee';
import EmployeesTable from '../components/Tables/EmployeesTable';
import { Button } from '@mui/material';
import { Box, Dialog, FormControl, InputAdornment, InputLabel, MenuItem, Paper, Select, Stack, TextField, useTheme } from '@mui/material';
import YesNoPopup from '../components/Popups/YesNoPopup';
import EmployeeForm from '../forms/EmployeeForm';
import { addEmployee, archiveEmployees, deleteEmployees, activeEmployees, editEmployee, listEmployeesWithFormationsAndAvailabilities, listFormations } from '../api/restaurant';
import CreateIcon from '@mui/icons-material/Create';
import dayjs from 'dayjs';
import { EmployeeLeaveDay } from '../forms/EmployeeLeaveDay';
import Formation from '../types/api/Formation';
import ArchiveIcon from '@mui/icons-material/Archive';
import SearchIcon from '@mui/icons-material/Search';
import MonthlyEventsCalendar from '../components/Calendar/MonthlyEventsCalendar';
import { EmployeeProfile } from '../components/Profile/EmployeeProfile';

enum EmployeeFormMode {
  add = 'add',
  edit = 'edit'
}

interface EquipeProps {
  
}

function Equipe(EquipeProps: EquipeProps) {
  const theme = useTheme();
  const [addEmployeePopup, setAddEmployeePopup] = useState(false);
  const [archiveEmployeePopup, setArchiveEmployeePopup] = useState(false);
  const [employeeLeaveDay, setEmployeeLeaveDay] = useState(false);
  const [employeeFormMode, setEmployeeFormMode] = useState(EmployeeFormMode.add);
  const [removeEmployeePopup, setRemoveEmployeePopup] = useState(false);
  
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]); // Id des employés sélectionnés
  const [selectedEmployee, setSelectedEmployee] = useState({id: -1, firstName: '', lastName: '', entryDate: new Date(Date.now()), hoursPerWeek: 0, hoursPerMonth: 0, hasWeeklyAvailabilities: false, isActive: true, phone: '', isFullTime: false, MondayNoon: false, MondayEvening: false, TuesdayNoon: false, TuesdayEvening: false, WednesdayNoon: false, WednesdayEvening: false, ThursdayNoon: false, ThursdayEvening: false, FridayNoon: false, FridayEvening: false, SaturdayNoon: false, SaturdayEvening: false, SundayNoon: false, SundayEvening: false, formations: [], WeeklyAvailabilities: [], isForeignStudent: false, foreignYearlyQuota: 0, visaExpirationDate: null, hourlyRate: 0, contrats: []} as Employee);

  const [employeeNameFilter, setEmployeeNameFilter] = useState("");
  const [employeesFiltered, setEmployeesFiltered] = useState<Employee[]>([]);

  const [formations, setFormations] = useState<Formation[]>([]);
  const [selectedFormation, setSelectedFormation] = useState<number | null>(null );

  const [activeEmployeePopup, setActiveEmployeePopup] = useState(false);
  const [employeeProfile, setEmployeeProfile] = useState(false);

  useEffect(() => {
    const fetchFormations = async () => {
      const res = await listFormations();
      if (res && res.data) {
        setFormations(res.data);
      }
    };
    fetchFormations();
  }, []);

  useEffect(() => {
    if (selectedFormation) {
      const filteredEmployees = employees.filter((employee) => {
        return employee.formations.some((formation) => formation.id === selectedFormation);
      });
      setEmployeesFiltered(filteredEmployees);
    } else {
      setEmployeesFiltered(employees);
    }
  }, [employees, selectedFormation]);

  useEffect(() => {
    const filteredEmployees = employees.filter((employee) => {
      const fullName = `${employee.lastName || ''} ${employee.firstName || ''}`.toLowerCase();
      return fullName.includes(employeeNameFilter.toLowerCase());
    });
    setEmployeesFiltered(filteredEmployees);
  }, [employees, employeeNameFilter]);

  const updateEmployees = async () => {
    const res = await listEmployeesWithFormationsAndAvailabilities();
    if (res && res.code === 200 && res.data) {
      //console.log(res.data)
      setEmployees(res.data);
    }
  };

  useEffect(() => {
    updateEmployees();
  }, []);

  const openEmployeeEdition = (employee: Employee) => {
    setEmployeeFormMode(EmployeeFormMode.edit);
    setSelectedEmployee(employees.find(e => e.id === employee.id) ?? employee);
    setAddEmployeePopup(true);
  };

  const openEmployeeLeaveDay = (employee: Employee) => {
    setSelectedEmployee(employee);
    setEmployeeLeaveDay(true);
  }

  const openArchiveEmployee = (employee: Employee) => {
    setSelectedEmployees([employee.id]);
    setArchiveEmployeePopup(true);
  }

  const openActiveEmployee = (employee: Employee) => {
    setSelectedEmployees([employee.id]);
    setActiveEmployeePopup(true);
  }

  const openEmployeeProfile = (employee: Employee) => {
    setSelectedEmployee(employees.find(e => e.id === employee.id) ?? employee);
    setEmployeeProfile(true);
  }

  const openDeleteEmployee = (employee: Employee) => {
    setSelectedEmployees([employee.id]);
    setRemoveEmployeePopup(true);
  }

  const saveEmployee = async (employee: Employee) => {
    const res = await addEmployee(employee);
    if(res && res.data){
      await updateEmployees();
      setAddEmployeePopup(false)
    }
  }

  const editCurrentEmployee = async (employee: Employee) => {
    const res = await editEmployee(employee);
    if(res && res.data){
      await updateEmployees();
      setAddEmployeePopup(false);
    }
  }

  const archiveEmployee = () => {
    setArchiveEmployeePopup(true);
  };


  const archiveSelectedEmployees = async () => {
    for (const employeeId of selectedEmployees) {
      const employee = employees.find(e => e.id === employeeId);
      if (employee) {
        const updatedEmployee = { ...employee, isActive: false };
        await archiveEmployees(updatedEmployee);
      }
    }
    await updateEmployees();
    setSelectedEmployees([]);
    setArchiveEmployeePopup(false);
  };

  const activeSelectedEmployee = async () => {
    for (const employeeId of selectedEmployees) {
      const employee = employees.find(e => e.id === employeeId);
      if (employee) {
        const updatedEmployee = { ...employee, isActive: true };
        await activeEmployees(updatedEmployee);
      }
    }
    await updateEmployees();
    setSelectedEmployees([]);
    setActiveEmployeePopup(false);
  };

  const deleteSelectedEmployees = async () => {
    await deleteEmployees(selectedEmployees);
    setSelectedEmployees([]);
     const res = await listEmployeesWithFormationsAndAvailabilities();
    if (res && res.code === 200 && res.data) {
      setEmployees(res.data);
      setRemoveEmployeePopup(false);
    }
  };


  const visaEvents = employees
    .filter((employee) => employee.isForeignStudent && employee.visaExpirationDate)
    .map((employee) => ({
      id: employee.id,
      name: `${employee.lastName.toUpperCase()} ${employee.firstName}`,
      date: new Date(employee.visaExpirationDate!),
      daysRemaining: dayjs(employee.visaExpirationDate).diff(dayjs(), 'day'),
      type: 'visa' as const,
      additionalInfo: `20h/${employee.foreignYearlyQuota}`,
    }));

  const trialEvents = employees
    .filter((employee) => {
      const daysSinceEntry = dayjs().diff(dayjs(employee.entryDate), 'day');
      return daysSinceEntry <= 60;
    })
    .map((employee) => {
      const entryDate = dayjs(employee.entryDate);
      const endTrialDate = entryDate.add(60, 'day');
      return {
        id: employee.id,
        name: `${employee.lastName.toUpperCase()} ${employee.firstName}`,
        date: endTrialDate.toDate(),
        daysRemaining: endTrialDate.diff(dayjs(), 'day'),
        type: 'trial' as const,
        additionalInfo: '',
      };
    });

  return (
    <>
      <MonthlyEventsCalendar 
        events={[...visaEvents, ...trialEvents]}
        title="Calendrier - Expirations de visa et fins de période d'essai"
      />

      <Box sx={{height: 16}} />

      <Paper variant='outlined' elevation={0} sx={{padding: 2}}>
        <Box sx={{display: "flex", flexDirection: "row", justifyContent: "space-between", marginBottom: 2}}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              slotProps={{
                input:{
                  startAdornment:(
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }
              }}
              id="filled"
              label="Rechercher un employé"
              style={{color : theme.palette.background.paper}}
              defaultValue={employeeNameFilter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmployeeNameFilter(event.target.value);
              }}
            />
            <FormControl sx={{ m: 1, minWidth: 120, color : theme.palette.background.paper }} >
              <InputLabel id="select-formation-label">
                Formation
              </InputLabel>
              <Select
                labelId="select-formation-label"
                id="select-formation"
                value={selectedFormation ?? ""}
                label="Formation"
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedFormation(value === "" ? null : Number(value));
                }}
              >
                <MenuItem value="">
                  <em>Tous</em>
                </MenuItem>
                {formations.map((formation) => (
                  <MenuItem key={formation.id} value={formation.id}>
                    {formation.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>


          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                setEmployeeFormMode(EmployeeFormMode.add);
                setSelectedEmployee({
                  id: -1,
                  firstName: '',
                  lastName: '',
                  entryDate: new Date(Date.now()),
                  hoursPerWeek: 0,
                  hoursPerMonth: 0,
                  hasWeeklyAvailabilities: false,
                  isActive: true,
                  phone: '',
                  isFullTime: false,
                  MondayNoon: false,
                  MondayEvening: false,
                  TuesdayNoon: false,
                  TuesdayEvening: false,
                  WednesdayNoon: false,
                  WednesdayEvening: false,
                  ThursdayNoon: false,
                  ThursdayEvening: false,
                  FridayNoon: false,
                  FridayEvening: false,
                  SaturdayNoon: false,
                  SaturdayEvening: false,
                  SundayNoon: false,
                  SundayEvening: false,
                  formations: [],
                  WeeklyAvailabilities: [],
                  isForeignStudent: false,
                  foreignYearlyQuota: 0,
                  visaExpirationDate: null,
                  hourlyRate: 0
                } as Employee);
                setAddEmployeePopup(!addEmployeePopup);
              }}
              variant="contained"
            >
              <CreateIcon />
              Ajouter un employé
            </Button>
            <Button
              onClick={archiveEmployee}
              variant="contained"
              color='error'
            >
              <ArchiveIcon />
              Archiver des employés
            </Button>
          </Stack>
        </Box>


        <EmployeesTable
          handleEmployeeEdition={openEmployeeEdition}
          handleLeaveDay={openEmployeeLeaveDay}
          handleArchiveEmployee={openArchiveEmployee}
          handleActiveEmployee={openActiveEmployee}
            handleViewProfile={openEmployeeProfile}
            handleDeleteEmployee={openDeleteEmployee}
          data={employeesFiltered}
          selectedEmployeeIds={selectedEmployees}
          onSelectionChange={setSelectedEmployees}
        />
      </Paper>

      <Box sx={{height: 32}} />

      <Dialog open={archiveEmployeePopup}>
        <YesNoPopup
          message="Voulez-vous vraiment archiver ce(s) employé(s) ?"
          no={() => {
            setArchiveEmployeePopup(false);
          }}
          yes={archiveSelectedEmployees}
        />
      </Dialog>

      <Dialog open={activeEmployeePopup}>
        <YesNoPopup
          message="Voulez-vous vraiment réactiver cet employé ?"
          no={() => {
            setActiveEmployeePopup(false);
          }}
          yes={activeSelectedEmployee}
        />
      </Dialog>

      <Dialog open={addEmployeePopup} maxWidth="lg" fullWidth>
        <EmployeeForm
          confirmLabel={employeeFormMode === EmployeeFormMode.add ? 'Créer' : 'Sauvegarder'}
          headerLabel={employeeFormMode === EmployeeFormMode.add ? 'Ajouter un employé' : 'Modifier un employé'}
          save={employeeFormMode === EmployeeFormMode.add ? saveEmployee : editCurrentEmployee}
          baseEmployee={selectedEmployee}
          close={() => {
            setAddEmployeePopup(false);
          }}
        />
      </Dialog>

      <Dialog open={employeeLeaveDay} maxWidth="lg">
        <EmployeeLeaveDay
          baseEmployee={selectedEmployee}
          closeDialog={() =>{setEmployeeLeaveDay(false)}}
        />
      </Dialog>

      <Dialog open={employeeProfile} maxWidth="lg">
        <EmployeeProfile
          baseEmployee={selectedEmployee}
          closeDialog={() =>{setEmployeeProfile(false)}}
        />
      </Dialog>
      <Dialog open={removeEmployeePopup}>
        <YesNoPopup
          message="Voulez-vous vraiment supprimer ce(s) employé(s) ?"
          no={() => {
            setRemoveEmployeePopup(false);
          }}
          yes={deleteSelectedEmployees}
        />
      </Dialog>
    </>
  );
}

export default Equipe;